import { FormType } from '@lenderful/domain';
import { routes } from 'app/routes/route-list';

/**
 * Takes in a url hash and returns the section id as an integer
 *
 * @param {string} hash Hash from the router location
 * @returns {number} Section ID
 */
export const sectionFromHash = (hash: string): number => {
  return parseFloat(hash.replace('#', ''));
};

const routeToFormTypeMap = {
  [routes.agricultural]            : FormType.AGRICULTURAL,
  [routes.agLineOfCredit]          : FormType.AGRICULTURAL_LINE_OF_CREDIT,
  [routes.agPurchaseAcreage]       : FormType.AGRICULTURAL_PURCHASE_ACREAGE,
  [routes.agPurchaseEquipment]     : FormType.AGRICULTURAL_PURCHASE_EQUIPMENT,
  [routes.agRefinanceProperty]     : FormType.AGRICULTURAL_REFINANCE_PROPERTY,
  [routes.application]             : FormType.LONG_FORM,
  [routes.applicationConstruction] : FormType.CONSTRUCTION,
  [routes.applicationHomeEquity]   : FormType.HOME_EQUITY_LONG_FORM,
  [routes.applicationLand]         : FormType.LAND,
  [routes.applicationPurchase]     : FormType.LONG_FORM,
  [routes.applicationRefinance]    : FormType.LONG_FORM,
  [routes.autoLoan]                : FormType.AUTO,
  [routes.autoLoanBasic]           : FormType.AUTO_BASIC,
  [routes.autoLoanInquiry]         : FormType.AUTO_LOAN_INQUIRY,
  [routes.autoPrequal]             : FormType.AUTO_PREQUAL,
  [routes.autoPrequalEdit]         : FormType.AUTO_PREQUAL,
  [routes.autoPrequalRenew]        : FormType.AUTO_PREQUAL,
  [routes.autoPrequalWelcome]      : FormType.AUTO_PREQUAL_WELCOME,
  [routes.closingCostsPurchase]    : FormType.CLOSING_COSTS,
  [routes.closingCostsRefinance]   : FormType.CLOSING_COSTS,
  [routes.commercial]              : FormType.COMMERCIAL,
  [routes.commercialBridgeLoan]    : FormType.COMMERCIAL_BRIDGE_LOAN,
  [routes.commercialEquipment]      : FormType.COMMERCIAL_EQUIPMENT,
  [routes.commercialLeaseAgFacilities] : FormType.COMMERCIAL_LEASE_AG_FACILITIES,
  [routes.commercialLeaseEquipment]    : FormType.COMMERCIAL_LEASE_EQUIPMENT,
  [routes.commercialLeaseMunicipal]    : FormType.COMMERCIAL_LEASE_MUNICIPAL,
  [routes.commercialPurchase]       : FormType.COMMERCIAL_PURCHASE,
  [routes.commercialRefinance]     : FormType.COMMERCIAL_REFINANCE,
  [routes.commercialVehicle]       : FormType.COMMERCIAL_VEHICLE,
  [routes.contact]                 : FormType.CONTACT,
  [routes.creditBooster]           : FormType.CREDIT_BOOSTER,
  [routes.creditCard]              : FormType.CREDIT_CARD,
  [routes.financialStatement]      : FormType.FINANCIAL_STATEMENT,
  [routes.homeEquity]              : FormType.HOME_EQUITY,
  [routes.homeEquityInquiry]       : FormType.HOME_EQUITY_INQUIRY,
  [routes.homeEquityRates]         : FormType.HOME_EQUITY_RATES,
  [routes.homeEquityTurbo]         : FormType.HOME_EQUITY_TURBO,
  [routes.homeEquityTurboWelcome]  : FormType.HOME_EQUITY_TURBO_WELCOME,
  [routes.insuranceQuote]          : FormType.INSURANCE_QUOTE,
  [routes.otherVehicle]            : FormType.OTHER_VEHICLE,
  [routes.otherVehicleBasic]       : FormType.OTHER_VEHICLE_BASIC,
  [routes.otherVehicleInquiry]     : FormType.OTHER_VEHICLE_INQUIRY,
  [routes.overdraftProtection]     : FormType.OVERDRAFT_PROTECTION,
  [routes.personalLoan]            : FormType.PERSONAL,
  [routes.personalLoanInquiry]     : FormType.PERSONAL_LOAN_INQUIRY,
  [routes.purchase]                : FormType.SHORT_FORM_PURCHASE,
  [routes.purchaseTurbo]           : FormType.PURCHASE_TURBO,
  [routes.refinance]               : FormType.SHORT_FORM_REFINANCE,
  [routes.refinanceTurbo]          : FormType.REFINANCE_TURBO,
  [routes.reverse]                 : FormType.REVERSE,
  [routes.sbLineOfCredit]          : FormType.SMALL_BUSINESS_LINE_OF_CREDIT,
  [routes.sbTerm]                  : FormType.SMALL_BUSINESS_TERM,
};

/**
 * Gets the form or product type from the path provided
 *
 * @param {string} pathname Name of the path
 * @returns {FormType | Product} Name of the form or product in ReduxForm({})
 */
export const getFormType = (pathname: string): FormType => {
  return routeToFormTypeMap[pathname];
};

/**
 * Used to determine if the pathname was previously a route that
 * contained a form.
 *
 * @param {string} pathname Name of the path
 * @returns {boolean} true if pathname can be found in routes containing forms, false otherwise
 */
export const containsFormType = (pathname: string): boolean => {
  const keys = Object.keys(routeToFormTypeMap);
  return keys.some(key => pathname.includes(key));
};

// Only works if the FormType to route is a 1 to 1
export const getRouteByFormType = (form: FormType): string => {
  return Object.keys(routeToFormTypeMap).find(key => routeToFormTypeMap[key] === form);
};
